import React from 'react';
import { AssistanceBenefitProps } from '../components/insurances/AssistanceBenefit';
import { EnumMap } from '../lib';

/* eslint-disable camelcase */
const tooGenders = ['male', 'female'] as const;

const tooAddressTypes = ['beneficiary', 'risk_site'] as const;

const tooAdditionalCoverages = [
    'lawsuit',
    'glassware',
    'property_theft',
    'leakage',
    'flooding',
    'electrical_damage',
    'gale',
    'rent_arrears',
] as const;

const tooCoverages = ['fire', ...tooAdditionalCoverages] as const;

const tooAssistances = ['none', 'basic', 'complete'] as const;

const tooChosenAssistance = ['yes', 'no'] as const;

const tooPropertyTypes = ['apartment', 'house'] as const;

const tooPropertyTypeToPt: EnumMap<typeof tooPropertyTypes> = {
    apartment: 'Apartamento',
    house: 'Casa',
};

const tooPepStatuses = ['self', 'related', 'none', 'undisclosed'] as const;

const tooInsurancePurchaseSteps = [
    'purpose',
    'rentalType',
    'riskSiteZipcode',
    'riskSiteAddress',
    'propertyType',
    'materialType',
    'rentValue',
    'basicCoverage',
    'additionalCoverage',
    'assistanceOption',
    'assistanceChoice',
    'beneficiaryCpf',
    'beneficiaryData',
    'quoteResult',
    'payment',
    'ticket',
    'finished',
] as const;

const tooServiceLevels = ['simple', 'basic', 'premium'] as const;

type TooGender = (typeof tooGenders)[number];

type TooAddress = (typeof tooAddressTypes)[number];

type TooAdditionalCoverage = (typeof tooAdditionalCoverages)[number];

type TooCoverage = (typeof tooCoverages)[number];

type TooAssistance = (typeof tooAssistances)[number];

type TooChosenAssistance = (typeof tooChosenAssistance)[number];

type TooProperty = (typeof tooPropertyTypes)[number];

type TooPepStatus = (typeof tooPepStatuses)[number];

type TooInsurancePurchaseStep = (typeof tooInsurancePurchaseSteps)[number];

type TooServiceLevel = (typeof tooServiceLevels)[number];

const residentialAssistanceFeatures = [
    { name: 'Assistência elétrica', support: ['basic', 'complete'] },
    { name: 'Vidraceiro', support: ['basic', 'complete'] },
    { name: 'Chaveiro', support: ['basic', 'complete'] },
    {
        name: 'Assistência hidráulica',
        support: ['basic', 'complete'],
    },
    {
        name: 'Dedetização',
        support: ['complete'],
    },
    { name: 'Telhas', support: ['complete'] },
    {
        name: 'Tubulações',
        support: ['complete'],
    },
    {
        name: 'Sistemas de computador',
        support: ['complete'],
    },
    {
        name: 'Inspeção',
        support: ['complete'],
    },
];

const residentialRebateFeatures = [
    {
        name: <>Suporte da Too Seguros</>,
        support: ['basic', 'premium', 'simple'],
    },
    {
        name: <>Atendimento da Alude via WhatsApp</>,
        support: ['basic', 'premium', 'simple'],
    },
    {
        name: (
            <>
                Prioridade <strong>dois</strong> nas chamadas
            </>
        ),
        support: ['premium', 'basic'],
    },
    {
        name: (
            <>
                Prioridade <strong>um</strong> nas chamadas
            </>
        ),
        support: ['premium'],
    },
];

const quoteChangeSets = {
    assistance: {
        none: {
            assistance: 'none' as const,
        },
        basic: {
            assistance: 'basic' as const,
        },
        complete: {
            assistance: 'complete' as const,
        },
    },
    rebate: {
        simple: {
            commissionValue: 50,
        },
        basic: {
            commissionValue: 30,
        },
        premium: {
            commissionValue: 50,
        },
    },
};

const tooAssistanceToPt = {
    basic: 'ASSISTÊNCIA BÁSICA',
    complete: 'ASSISTÊNCIA PREMIUM',
    none: 'SEM ASSISTÊNCIA',
};

const tooServiceLevelToPt = {
    basic: 'SUPORTE BÁSICO',
    premium: 'SUPORTE PREMIUM',
    simple: 'SUPORTE SIMPLES',
};

const tooCoverageToPt: Record<TooCoverage, string> = {
    fire: 'Básica',
    lawsuit: 'Responsabilidade civil',
    glassware: 'Vidros, espelhos e mármores',
    property_theft: 'Roubo de bens',
    leakage: 'Vazamento de tubulações',
    flooding: 'Alagamento e inundação',
    electrical_damage: 'Danos elétricos',
    gale: 'Vendaval e impacto de veículo terrestre',
    rent_arrears: 'Perda ou pagamento de aluguel',
};

const tooCoverageDescriptions: Record<TooCoverage, string> = {
    fire: 'Garante ao segurado indenização pelos danos materiais causados aos bens por incêndio, explosão, queda de raio, fumaça e queda de aeronave.',
    lawsuit:
        'Garante indenização pelos danos involuntários, corporais ou materiais causados a terceiros pelo próprio segurado, seu cônjuge, demais moradores da residência segurada ou empregados no exercício do trabalho.',
    glassware:
        'Garante ao segurado indenização pelos danos, quando fixados permanentemente, a vidros, espelhos ou mármores.',
    property_theft:
        'Garante indenização ao segurado por danos causados por roubo ou furto qualificado de valores - com destruição ou uso de força.',
    leakage:
        'Garante ao segurado indenização pelos danos materiais causados à residência segurada e ao seu conteúdo por vazamento d`àgua oriundo do rompimento acidental de canalização residencial.',
    flooding:
        'Garante ao segurado indenização pelos danos causados por enchentes, aguaceiros ou inundações.',
    electrical_damage:
        'Garante indenização ao segurado pelos danos materiais causados a máquinas, equipamentos, instalações elétricas ou eletrônicas por problemas elétricos.',
    gale: 'Garante ao segurado indenização pelos danos materiais causados à residência consequentes de vendaval, furacão, ciclone, tornado, granizo ou impacto de veículos terrestres.',
    rent_arrears:
        'Caso o imóvel fique inabitável por razão de risco coberto, garante até 6 meses de pagamento de aluguel ao locador, que deixará de receber aluguel, ou ao locatário, que terá que pagar aluguel em outro imóvel.',
};

const tooAssistanceBenefits: Record<TooAssistance, AssistanceBenefitProps[]> = {
    basic: [
        {
            art: 'DoorLock',
            title: 'Chaveiro',
            description:
                'Conserto ou confecção de chave para porta de acesso à residência.',
        },
        {
            art: 'LeakRepair',
            title: 'Mão de obra hidráulica',
            description:
                'Solução de problemas de vazamento em tubulações ou problemas que possam causar risco de alagamento.',
        },
        {
            art: 'ElectricWire',
            title: 'Mão de obra elétrica',
            description:
                'Solução de problemas elétricos simples como: tomadas queimadas, interruptores defeituosos e troca de chuveiros.',
        },
        {
            art: 'PoliceBadge',
            title: 'Vigilância',
            description:
                'Envio de vigilância em caso de vulnerabilidade da residência segurada, após tentativa de contenção do local de risco.',
        },
    ],
    none: [],
    complete: [
        {
            art: 'DoorLock',
            title: 'Chaveiro',
            description:
                'Conserto ou confecção de chave para porta de acesso à residência.',
        },
        {
            art: 'LeakRepair',
            title: 'Mão de obra hidráulica',
            description:
                'Solução de problemas de vazamento em tubulações ou problemas que possam causar risco de alagamento.',
        },
        {
            art: 'ElectricWire',
            title: 'Mão de obra elétrica',
            description:
                'Solução de problemas elétricos simples como: tomadas queimadas, interruptores defeituosos e troca de chuveiros.',
        },
        {
            art: 'PoliceBadge',
            title: 'Vigilância',
            description:
                'Envio de vigilância em caso de vulnerabilidade da residência segurada, após tentativa de contenção do local de risco.',
        },
        {
            art: 'Window',
            title: 'Vidraceiro',
            description:
                'Conserto de portas e janelas externas que possam gerar situação de risco.',
        },
        {
            art: 'HouseLove',
            title: 'Limpeza de residência',
            description:
                'Limpeza emergencial para dar condições de habitação à residência.',
        },
        {
            art: 'FornitureTransport',
            title: 'Mudança e guarda-móveis',
            description:
                'Serviço de mudança e guarda-móveis em caso de sinistro que demande reforma ou reparos à residência.',
        },
        {
            art: 'PropertyDamage',
            title: 'Serviços domésticos provisórios',
            description:
                'Despesas com faxineira, caso o segurado sofra danos físicos no imóvel e fique hospitalizado por 5 dias ou mais.',
        },
        {
            art: 'Building',
            title: 'Hospedagem',
            description:
                'Pagamento de até 4 dias de hospedagem para segurado e familiares em caso de sinistro que impossibilite a habitação do imóvel',
        },
        {
            art: 'Ticket',
            title: 'Regresso antecipado',
            description:
                'Passagem aérea ou outro meio de transporte, a critério da empresa Tempo Assist, para o retorno do segurado em caso de problema emergencial na residência segurada.',
        },
        {
            art: 'Ambulance',
            title: 'Remoção inter-hospitalar',
            description:
                'Transferência com meio de transporte mais adequado, caso o segurado seja hospitalizado por razão do sinistro e precise ser transferido para hospital mais estruturado.',
        },
        {
            art: 'Vacation',
            title: 'Restaurante e lavanderia',
            description:
                'Pagamento de despesas com restaurante e lavanderia caso esses espaços sejam inutilizados devido a sinistro.',
        },
        {
            art: 'LeakRepair',
            title: 'Desentupimento de tubulação e caixa de esgoto',
            description:
                'Reparo em caso de vazamento ou entupimento em tubulações de esgoto e caixa de gordura que possam causar alagamento da residência',
        },
        {
            art: 'Antenna',
            title: 'Fixação ou retirada de antenas',
            description:
                'Envio de profissional para fixar ou retirar antena, caso haja risco de queda.',
        },
        {
            art: 'Mother',
            title: 'Babá',
            description:
                'Pagamento de profissional para cuidar de menores de 14 anos caso o segurado seja hospitalizado em razão de sinistro no imóvel.',
        },
        {
            art: 'Scales',
            title: 'Orçamento para manutenções gerais',
            description:
                'Orçamento gratuito de custos de material e mão de obra em manutenções gerais.',
        },
    ],
};

const getServiceLevel = (commissionValue: number): TooServiceLevel => {
    if (commissionValue < 17) {
        return 'simple';
    } else if (commissionValue >= 17 && commissionValue <= 33) {
        return 'basic';
    }
    return 'premium';
};

const tooCoverageBenefits: Record<TooCoverage, string[]> = {
    fire: ['Danos por incêndio, explosão e fumaça'],
    flooding: ['Entrada de água no imóvel'],
    leakage: ['Vazamento de tubulações'],
    gale: ['Vendaval e impacto de veículos'],
    rent_arrears: ['Perda de aluguel ou pagamento de aluguel'],
    property_theft: ['Roubo de bens'],
    electrical_damage: ['Danos elétricos gerais'],
    glassware: ['Quebra de vidros, espelhos e mármores'],
    lawsuit: [
        'Danos físicos à pessoa e dano material',
        'Danos a terceiros causados por animal doméstico',
        'Gastos com honorários de advogados',
        'Danos por vazamento de água',
    ],
};

export {
    tooCoverageBenefits,
    tooGenders,
    tooAddressTypes,
    getServiceLevel,
    tooCoverages,
    tooAssistances,
    tooChosenAssistance,
    tooPropertyTypes,
    tooPepStatuses,
    tooAssistanceToPt,
    tooInsurancePurchaseSteps,
    tooAdditionalCoverages,
    residentialRebateFeatures,
    residentialAssistanceFeatures,
    quoteChangeSets,
    tooCoverageToPt,
    tooCoverageDescriptions,
    tooAssistanceBenefits,
    tooPropertyTypeToPt,
    tooServiceLevelToPt,
};
export type {
    TooGender,
    TooAddress,
    TooCoverage,
    TooAssistance,
    TooChosenAssistance,
    TooAdditionalCoverage,
    TooProperty,
    TooPepStatus,
    TooInsurancePurchaseStep,
    TooServiceLevel,
};
